/** @prettier */

/**
 * 通信系統番号一覧
 */

import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {Card, CardContent, Stack, Box} from '@mui/material';
import {useLoaderData, Link, useLocation} from 'react-router-dom';

//MTH component import
import SLSetting from './SLSetting';
import RTSetting from './RTSetting';
import CardContents from '../../component/CardContents';
import GWConfigUpdateDrawer from '../gw-registration/GWConfigUpdateDrawer';
//API import
import {getCompositionsGateway} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';

import {gwInfoSort} from '../../functions/sort';
import {useOutlet} from '../gw-registration/GWMain';
import {checkUnitExistenceByGatewayID} from '../../functions/checkUnitExistence';

type loaderReturn = {
  /** サイトに登録されているゲートウェイの情報 */
  allGWInfo: apiIF.res.getCompositionsGateway;
  /** 表示しているゲートウェイの情報 */
  displayGWInfo: apiIF.resParameters.gatewayInformations;
  /** 登録ユニットの有無 */
  hasUnit: boolean;
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<loaderReturn | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined || params.gwID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //ゲートウェイの情報を取得
    promises.push(
      getCompositionsGateway(params.siteID, {
        'max-result': 100,
      })
    );
    // ゲートウェイの機器登録状況を取得
    promises.push(checkUnitExistenceByGatewayID(params.siteID, params.gwID));
    const response = await Promise.all(promises);
    if (response[0] === undefined) return undefined;
    //表示内容をUnicode順に並び替え
    const allGWInfo = gwInfoSort(
      response[0] as apiIF.res.getCompositionsGateway
    );

    //表示しているゲートウェイの情報を検索
    const temp = allGWInfo['gateway-informations'].find(
      gwInfo => gwInfo['gateway-id'] === params.gwID
    );
    if (temp === undefined) return undefined;

    /** ユニットの登録状況 */
    const unitExistence = response[1] as boolean;

    return {
      allGWInfo: allGWInfo,
      displayGWInfo: temp,
      hasUnit: unitExistence,
    };
  } catch {
    return undefined;
  }
}

const Communication = () => {
  const trans = useTranslation().t; //翻訳関数
  const gwInfo = useLoaderData() as loaderReturn | undefined;
  //親コンポーネントから値を取得
  const {setAllGWInfo} = useOutlet();
  //URL取得
  const location = useLocation();

  const [hasConfigGW, setHasConfigGW] = useState<boolean>(false); //GW毎の構成情報未反映の有無
  const [hasUnit, setHasUnit] = useState<boolean>(false); //GWの機器登録有無

  useEffect(() => {
    if (gwInfo !== undefined) {
      setAllGWInfo(gwInfo.allGWInfo);
      setHasUnit(gwInfo.hasUnit);
      //構成情報未反映の有無をチェック
      setHasConfigGW(
        gwInfo.displayGWInfo['billing-status'] !== undefined &&
          gwInfo.displayGWInfo['billing-status'] !== 'unpaid' &&
          gwInfo.displayGWInfo['composition-receipt-version'] !== undefined &&
          gwInfo.displayGWInfo['composition-receipt-version'] !== 'latest'
      );
    }
  }, [gwInfo]);

  //ゲートウェイの情報を取得できない場合
  if (gwInfo === undefined) {
    return <></>;
  }

  //Modbus空調機の台数をカウント
  let modbusOUNum = '0'; //modbus室外機台数
  let modbusIUNum = '0'; //modbus室内機台数
  if (gwInfo.displayGWInfo['register-number'] !== undefined) {
    modbusOUNum =
      gwInfo.displayGWInfo['register-number']['modbus-system']['outdoor-unit'];
    modbusIUNum =
      gwInfo.displayGWInfo['register-number']['modbus-system']['indoor-unit'];
  }
  return (
    <>
      {/* ゲートウェイ名称の表示 */}
      <Box
        className="gwSetting__title"
        title={gwInfo.displayGWInfo['gateway-name']}>
        {gwInfo.displayGWInfo['gateway-name']}
      </Box>
      {/* 通信設定表示 */}
      <Stack spacing={4}>
        {gwInfo.displayGWInfo.system === 'sl' ? (
          <SLSetting gwInfo={gwInfo.displayGWInfo} />
        ) : (
          <RTSetting />
        )}
        {/* Modbus */}
        {/* 有効の場合のみ表示 */}
        {gwInfo.displayGWInfo['modbus-tcp'] === 'active' ? (
          <Card className="gwSetting__contents--communication">
            <CardContent>
              <Stack spacing={4}>
                <Box className="gwSetting__contents__title">
                  <Link
                    to={`${location.pathname}/modbus/modbus-ou-registration${location.search}`}>
                    {trans('Communication.linkTxt.modbus')}
                  </Link>
                </Box>
                {/* 台数 */}
                <Stack spacing={2} className="gwSetting__contents__contents">
                  {/* 室内機数 */}
                  <CardContents
                    class="gwSetting__contents__contents__underLine"
                    valueDescription={trans(
                      'Communication.item.title.indoorUnit'
                    )}
                    value={`${modbusIUNum} ${trans(
                      'Communication.item.value.unit'
                    )}`}
                  />
                  {/* 室外機数 */}
                  <CardContents
                    class="gwSetting__contents__contents__underLine"
                    valueDescription={trans(
                      'Communication.item.title.outdoorUnit'
                    )}
                    value={`${modbusOUNum} ${trans(
                      'Communication.item.value.unit'
                    )}`}
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <></>
        )}
      </Stack>
      {/* 構成情報反映ボタン */}
      <GWConfigUpdateDrawer
        hasConfigGW={hasConfigGW}
        setHasConfigGW={setHasConfigGW}
        hasUnit={hasUnit}
      />
    </>
  );
};
export default Communication;
